import { c as _c } from "react/compiler-runtime";
import { patchOrgProxyConfigsByProxyConfigIdMutation, postOrgProxyConfigsMutation } from '@onefootprint/fetch-dashboard';
import { useRequestErrorToast } from '@onefootprint/hooks';
import { IcoChevronLeftBig16, IcoClose16 } from '@onefootprint/icons';
import { Dialog as FPDialog, useConfirmationDialog, useToast } from '@onefootprint/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { EditSectionId, FormData } from '@/pages/developers/vault-proxy/pages/list/vault-proxy.types';
import steps from './constants';
import FormWizard from './form-wizard';
import formatCreatePayload from './form-wizard/format-create-payload';
import formatUpdatePayload from './utils/format-update-payload';
type DialogProps = {
  defaultValues: FormData;
  onClose: () => void;
  open: boolean;
  editMode?: {
    proxyConfigId: string;
    stepId: EditSectionId;
  };
};
const Dialog = t0 => {
  const $ = _c(77);
  const {
    onClose,
    open,
    defaultValues,
    editMode
  } = t0;
  const {
    t: allT
  } = useTranslation("common");
  const {
    t
  } = useTranslation("vault-proxy");
  const isEditMode = !!editMode;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = postOrgProxyConfigsMutation({});
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const createMutation = useMutation(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = patchOrgProxyConfigsByProxyConfigIdMutation({});
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const updateMutation = useMutation(t2);
  const mutation = isEditMode ? updateMutation : createMutation;
  const confirmationDialog = useConfirmationDialog();
  const {
    toast
  } = useToast();
  const showRequestErrorToast = useRequestErrorToast();
  const queryClient = useQueryClient();
  let t3;
  if ($[2] !== editMode || $[3] !== isEditMode) {
    t3 = isEditMode ? steps.findIndex(step => step.id === editMode.stepId) : 0;
    $[2] = editMode;
    $[3] = isEditMode;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const initialStepIndex = t3;
  const [stepIndex, setStepIndex] = useState(initialStepIndex);
  const isFirstStep = stepIndex === 0;
  const isLastStep = stepIndex === steps.length - 1;
  const maxStep = steps.length - 1;
  const {
    Component,
    id,
    canSkip
  } = steps[stepIndex];
  let t4;
  let t5;
  if ($[5] !== initialStepIndex || $[6] !== open) {
    t4 = () => {
      if (open) {
        setStepIndex(initialStepIndex);
      } else {
        setStepIndex(0);
      }
    };
    t5 = [open, initialStepIndex];
    $[5] = initialStepIndex;
    $[6] = open;
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  useEffect(t4, t5);
  let t6;
  if ($[9] !== stepIndex) {
    t6 = () => {
      if (stepIndex < maxStep) {
        setStepIndex(_temp);
      }
    };
    $[9] = stepIndex;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const goForward = t6;
  let t7;
  if ($[11] !== stepIndex) {
    t7 = () => {
      if (stepIndex > 0) {
        setStepIndex(_temp2);
      }
    };
    $[11] = stepIndex;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const goBack = t7;
  let t8;
  if ($[13] !== allT || $[14] !== confirmationDialog || $[15] !== onClose) {
    t8 = () => {
      confirmationDialog.open({
        title: allT(_temp3),
        description: allT(_temp4),
        primaryButton: {
          label: allT(_temp5),
          onClick: onClose
        },
        secondaryButton: {
          label: allT(_temp6)
        }
      });
    };
    $[13] = allT;
    $[14] = confirmationDialog;
    $[15] = onClose;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const confirmBeforeClosing = t8;
  let t9;
  if ($[17] !== createMutation || $[18] !== defaultValues || $[19] !== editMode || $[20] !== isEditMode || $[21] !== onClose || $[22] !== queryClient || $[23] !== showRequestErrorToast || $[24] !== t || $[25] !== toast || $[26] !== updateMutation) {
    t9 = async formData => {
      if (isEditMode && editMode) {
        const originalSecretHeaders = defaultValues.headers.filter(_temp7).map(_temp8);
        const payload = formatUpdatePayload(editMode.stepId, formData, originalSecretHeaders);
        await updateMutation.mutateAsync({
          path: {
            proxyConfigId: editMode.proxyConfigId
          },
          body: payload
        }, {
          onError: error => {
            showRequestErrorToast(error);
          },
          onSuccess: response => {
            toast({
              title: t(_temp9),
              description: t(_temp10, {
                name: response.name
              })
            });
            queryClient.invalidateQueries();
            onClose();
          }
        });
      } else {
        const payload_0 = formatCreatePayload(formData);
        await createMutation.mutateAsync({
          body: payload_0
        }, {
          onError: error_0 => {
            showRequestErrorToast(error_0);
          },
          onSuccess: response_0 => {
            toast({
              title: t(_temp11),
              description: t(_temp12, {
                name: response_0.name
              })
            });
            queryClient.invalidateQueries();
            onClose();
          }
        });
      }
    };
    $[17] = createMutation;
    $[18] = defaultValues;
    $[19] = editMode;
    $[20] = isEditMode;
    $[21] = onClose;
    $[22] = queryClient;
    $[23] = showRequestErrorToast;
    $[24] = t;
    $[25] = toast;
    $[26] = updateMutation;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  const handleSubmit = t9;
  let t10;
  if ($[28] !== allT || $[29] !== isEditMode || $[30] !== isLastStep || $[31] !== t) {
    t10 = () => {
      if (isEditMode) {
        return allT(_temp13);
      }
      if (isLastStep) {
        return t(_temp14);
      }
      return allT(_temp15);
    };
    $[28] = allT;
    $[29] = isEditMode;
    $[30] = isLastStep;
    $[31] = t;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  const getPrimaryLabel = t10;
  let t11;
  if ($[33] !== allT || $[34] !== isEditMode || $[35] !== isFirstStep) {
    t11 = isEditMode || isFirstStep ? allT(_temp16) : allT(_temp17);
    $[33] = allT;
    $[34] = isEditMode;
    $[35] = isFirstStep;
    $[36] = t11;
  } else {
    t11 = $[36];
  }
  const t12 = isEditMode || isFirstStep ? IcoClose16 : IcoChevronLeftBig16;
  const t13 = isEditMode || isFirstStep ? onClose : goBack;
  let t14;
  if ($[37] !== t11 || $[38] !== t12 || $[39] !== t13) {
    t14 = {
      ariaLabel: t11,
      component: t12,
      onClick: t13
    };
    $[37] = t11;
    $[38] = t12;
    $[39] = t13;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  const t15 = isEditMode || isFirstStep ? onClose : confirmBeforeClosing;
  let t16;
  if ($[41] !== isEditMode || $[42] !== t) {
    t16 = isEditMode ? t(_temp18) : t(_temp19);
    $[41] = isEditMode;
    $[42] = t;
    $[43] = t16;
  } else {
    t16 = $[43];
  }
  let t17;
  if ($[44] !== allT || $[45] !== canSkip || $[46] !== goForward || $[47] !== isEditMode) {
    t17 = !isEditMode && canSkip ? {
      label: allT(_temp20),
      onClick: goForward
    } : undefined;
    $[44] = allT;
    $[45] = canSkip;
    $[46] = goForward;
    $[47] = isEditMode;
    $[48] = t17;
  } else {
    t17 = $[48];
  }
  let t18;
  if ($[49] !== getPrimaryLabel) {
    t18 = getPrimaryLabel();
    $[49] = getPrimaryLabel;
    $[50] = t18;
  } else {
    t18 = $[50];
  }
  let t19;
  if ($[51] !== id || $[52] !== mutation.isPending || $[53] !== t18) {
    t19 = {
      form: id,
      label: t18,
      loading: mutation.isPending,
      type: "submit"
    };
    $[51] = id;
    $[52] = mutation.isPending;
    $[53] = t18;
    $[54] = t19;
  } else {
    t19 = $[54];
  }
  const t20 = mutation.isPending;
  let t21;
  if ($[55] !== allT) {
    t21 = allT(_temp21);
    $[55] = allT;
    $[56] = t21;
  } else {
    t21 = $[56];
  }
  const t22 = isEditMode || isFirstStep ? onClose : confirmBeforeClosing;
  let t23;
  if ($[57] !== mutation.isPending || $[58] !== t21 || $[59] !== t22) {
    t23 = {
      disabled: t20,
      label: t21,
      onClick: t22
    };
    $[57] = mutation.isPending;
    $[58] = t21;
    $[59] = t22;
    $[60] = t23;
  } else {
    t23 = $[60];
  }
  const t24 = isEditMode || isLastStep;
  let t25;
  if ($[61] !== Component || $[62] !== defaultValues || $[63] !== goForward || $[64] !== handleSubmit || $[65] !== id || $[66] !== t24) {
    t25 = <FormWizard Component={Component} defaultValues={defaultValues} id={id} isLastStep={t24} onForward={goForward} onSubmit={handleSubmit} />;
    $[61] = Component;
    $[62] = defaultValues;
    $[63] = goForward;
    $[64] = handleSubmit;
    $[65] = id;
    $[66] = t24;
    $[67] = t25;
  } else {
    t25 = $[67];
  }
  let t26;
  if ($[68] !== open || $[69] !== t14 || $[70] !== t15 || $[71] !== t16 || $[72] !== t17 || $[73] !== t19 || $[74] !== t23 || $[75] !== t25) {
    t26 = <FPDialog headerIcon={t14} onClose={t15} open={open} size="compact" title={t16} linkButton={t17} primaryButton={t19} secondaryButton={t23}>{t25}</FPDialog>;
    $[68] = open;
    $[69] = t14;
    $[70] = t15;
    $[71] = t16;
    $[72] = t17;
    $[73] = t19;
    $[74] = t23;
    $[75] = t25;
    $[76] = t26;
  } else {
    t26 = $[76];
  }
  return t26;
};
export default Dialog;
function _temp(prevStep) {
  return prevStep + 1;
}
function _temp2(prevStep_0) {
  return prevStep_0 - 1;
}
function _temp3($) {
  return $.confirm.title;
}
function _temp4($_0) {
  return $_0.confirm.description;
}
function _temp5($_1) {
  return $_1.confirm.cta;
}
function _temp6($_2) {
  return $_2.confirm.cancel;
}
function _temp7(header) {
  return header.secret && header.id;
}
function _temp8(header_0) {
  return {
    id: header_0.id as string,
    name: header_0.name
  };
}
function _temp9($_3) {
  return $_3.notifications.edit.success.title;
}
function _temp10($_4) {
  return $_4.notifications.edit.success.description;
}
function _temp11($_5) {
  return $_5.notifications.create.success.title;
}
function _temp12($_6) {
  return $_6.notifications.create.success.description;
}
function _temp13($_7) {
  return $_7.save;
}
function _temp14($_8) {
  return $_8.create.form.cta;
}
function _temp15($_9) {
  return $_9.next;
}
function _temp16($_10) {
  return $_10.close;
}
function _temp17($_11) {
  return $_11.back;
}
function _temp18($_12) {
  return $_12.edit.form.title;
}
function _temp19($_13) {
  return $_13.create.form.title;
}
function _temp20($_14) {
  return $_14.skip;
}
function _temp21($_15) {
  return $_15.cancel;
}